/**
 * ContactsApiClient
 * 
 * Handles contacts, conversations, and messaging
 */

import { BaseApiClient } from './BaseApiClient';
import type { Contact, ConversationMessage } from '../../types/api/user';

export class ContactsApiClient extends BaseApiClient {
  /**
   * Get all contacts (counterparties)
   * Uses v2 API endpoint: GET /api/v2/contacts
   * 
   * Query Parameters:
   * - role: Filter by role (MERCHANT or USER)
   * - includeTransactions: Include transaction IDs and dates (default: false)
   * 
   * Response format:
   * {
   *   "contacts": [...],
   *   "count": number
   * }
   * 
   * Reference: API-REFERENCE-MANUAL.md line 3242
   */
  private rawApiResponse: any = null;
  
  getRawApiResponse(): any {
    return this.rawApiResponse;
  }
  
  /**
   * Get a single contact by user ID
   * Uses v2 API endpoint: GET /api/v2/contacts?userId={userId}
   * 
   * When userId is provided, the API returns a single contact object:
   * {
   *   "contact": {...},
   *   "count": 1
   * }
   * 
   * Returns null if contact not found (404 response)
   * 
   * Reference: API-REFERENCE-MANUAL.md line 3242
   */
  async getContact(userId: string, options?: {
    includeTransactions?: boolean;
  }): Promise<Contact | null> {
    const params = new URLSearchParams();
    params.append('userId', userId);
    if (options?.includeTransactions !== false) {
      params.append('includeTransactions', 'true');
    }
    
    const endpoint = `v2/contacts?${params.toString()}`;
    
    try {
      // When userId is provided, API returns { contact: {...}, count: 1 }
      const result = await this.get<{
        contact: any;
        count: number;
      }>(endpoint);
      
      if (result?.contact) {
        return this.mapContactFromApi(result.contact);
      }
      
      return null;
    } catch (error: any) {
      // 404 means contact not found or not a counterparty
      if (error?.status === 404 || error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
  
  async getContacts(options?: {
    role?: 'MERCHANT' | 'USER';
    includeTransactions?: boolean;
  }): Promise<Contact[]> {
    const params = new URLSearchParams();
    if (options?.role) {
      params.append('role', options.role);
    }
    // Always request transactions to get transaction counts and dates for proper UI display
    params.append('includeTransactions', 'true');
    
    const queryString = params.toString();
    const endpoint = `v2/contacts${queryString ? `?${queryString}` : ''}`;
    
    // BaseApiClient will detect v2/ prefix and use /api/ prefix (resulting in /api/v2/contacts)
    const result = await this.get<{
      contacts: any[];
      count: number;
    }>(endpoint);
    
    // Store raw response for debugging
    this.rawApiResponse = result;
    
    // DEBUG: Log raw API response
    console.log('🔍 [ContactsApiClient] Raw API response:', JSON.stringify(result, null, 2));
    console.log('🔍 [ContactsApiClient] Contacts array:', result?.contacts);
    if (result?.contacts && result.contacts.length > 0) {
      console.log('🔍 [ContactsApiClient] First contact sample:', JSON.stringify(result.contacts[0], null, 2));
    }
    
    // API returns { contacts: [...], count: number }
    if (result?.contacts && Array.isArray(result.contacts)) {
      // Map API response to Contact interface
      const mappedContacts = result.contacts.map((contact: any, index: number) => {
        // DEBUG: Log each contact mapping
        console.log(`🔍 [ContactsApiClient] Mapping contact ${index}:`, {
          raw: contact,
          profile: contact.profile,
          transactions: contact.transactions,
          role: contact.role,
          userId: contact.userId,
          id: contact.id
        });
        return this.mapContactFromApi(contact, index);
      });
      
      // DEBUG: Log final mapped contacts
      console.log('🔍 [ContactsApiClient] Final mapped contacts:', mappedContacts);
      return mappedContacts;
    }
    
    console.warn('⚠️ [ContactsApiClient] No contacts array in response:', result);
    return [];
  }
  
  /**
   * Map a single contact from API format to Contact interface
   * Handles the improved API response structure with transactionCount field
   */
  private mapContactFromApi(contact: any, index?: number): Contact {
    // Map from API format to Contact interface
    const profile = contact.profile || {};
    const transactions = contact.transactions || [];
    const isMerchant = Array.isArray(contact.role) && contact.role.includes('MERCHANT');
    // Use transactionCount from API if available (improved endpoint), otherwise calculate from transactions array
    const transactionCount = contact.transactionCount ?? transactions.length ?? 0;
    const lastTransactionDate = contact.lastTransactionDate ?? 
      (transactions.length > 0 ? transactions[transactions.length - 1].date : undefined);
    
    // Extract name - prefer display name fields, avoid using username as name
    const displayName = profile.name || 
                       contact.name || 
                       (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}`.trim() : null) ||
                       profile.firstName || 
                       profile.lastName ||
                       contact.displayName ||
                       'Unknown';
    
    // Only use username as name if it's clearly not a username (doesn't contain underscores/hyphens typical of usernames)
    // or if no other name is available
    const finalName = displayName === 'Unknown' && contact.username 
      ? contact.username 
      : displayName;
    
    const mappedContact = {
      id: contact.userId || contact.id,
      name: finalName,
      email: profile.email || contact.email,
      phoneNumber: profile.phone || contact.phoneNumber,
      username: profile.username || contact.username,
      totalTransactions: transactionCount,
      totalSent: contact.totalSent || 0,
      totalReceived: contact.totalReceived || 0,
      lastTransactionDate: lastTransactionDate,
      firstTransactionDate: transactions.length > 0 
        ? transactions[0].date 
        : contact.firstTransactionDate,
      relationship: this.determineRelationship(transactionCount, lastTransactionDate, isMerchant),
      hasConversations: contact.hasConversations || false,
      conversationCount: contact.conversationCount || 0,
      merchantId: isMerchant ? (contact.userId || contact.id) : undefined,
      avatar: profile.avatar || contact.avatar,
      transactions: transactions // Store full transactions array for currency-aware calculations
    };
    
    // DEBUG: Log mapped contact if index provided
    if (index !== undefined) {
      console.log(`🔍 [ContactsApiClient] Mapped contact ${index}:`, {
        id: mappedContact.id,
        name: mappedContact.name,
        totalTransactions: mappedContact.totalTransactions,
        lastTransactionDate: mappedContact.lastTransactionDate,
        relationship: mappedContact.relationship
      });
    }
    
    return mappedContact;
  }
  
  /**
   * Determine relationship category based on transaction count, recency, and merchant status
   */
  private determineRelationship(
    transactionCount: number, 
    lastTransactionDate?: string,
    isMerchant?: boolean
  ): 'frequent' | 'recent' | 'business' | 'infrequent' {
    // Business contacts (merchants) are always categorized as 'business'
    if (isMerchant) {
      return 'business';
    }
    
    // Frequent contacts have 10+ transactions
    if (transactionCount >= 10) {
      return 'frequent';
    }
    
    // Recent contacts have transactions within the last 7 days
    if (lastTransactionDate) {
      const daysSinceLastTransaction = Math.floor(
        (Date.now() - new Date(lastTransactionDate).getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSinceLastTransaction <= 7) {
        return 'recent';
      }
    }
    
    // Default to infrequent for contacts with few or old transactions
    return 'infrequent';
  }

  /**
   * Add new contact
   */
  async addContact(contact: { name: string; email?: string; phoneNumber?: string }): Promise<Contact> {
    const result = await this.post<{ contact: Contact }>('/contacts', contact);
    return result.contact;
  }

  /**
   * Search contacts
   */
  async searchContacts(searchTerm: string): Promise<Contact[]> {
    const result = await this.get<{ contacts: Contact[] }>(`/contacts/search?q=${encodeURIComponent(searchTerm)}`);
    return result.contacts;
  }

  /**
   * Update contact
   */
  async updateContact(contactId: string, updates: Partial<Contact>): Promise<Contact> {
    const result = await this.put<{ contact: Contact }>(`/contacts/${contactId}`, updates);
    return result.contact;
  }

  /**
   * Delete contact
   */
  async deleteContact(contactId: string): Promise<void> {
    await this.delete(`/contacts/${contactId}`);
  }

  /**
   * Get contact groups
   */
  async getContactGroups(): Promise<Array<{
    id: string;
    name: string;
    contactCount: number;
    createdAt: string;
  }>> {
    const result = await this.get<{ groups: any[] }>('/contacts/groups');
    return result.groups;
  }

  /**
   * Create contact group
   */
  async createContactGroup(name: string, contactIds: string[]): Promise<void> {
    await this.post('/contacts/groups', { name, contactIds });
  }

  /**
   * Get conversations
   */
  async getConversations(filters?: {
    transactionId?: string;
    contactId?: string;
    status?: 'active' | 'archived';
  }): Promise<ConversationMessage[]> {
    const params = new URLSearchParams();
    if (filters?.transactionId) params.append('transactionId', filters.transactionId);
    if (filters?.contactId) params.append('contactId', filters.contactId);
    if (filters?.status) params.append('status', filters.status);
    
    const result = await this.get<{ conversations: ConversationMessage[] }>(`/conversations?${params}`);
    return result.conversations;
  }

  /**
   * Get conversation for specific transaction (v1 - legacy)
   */
  async getConversation(transactionId: string): Promise<any[]> {
    const result = await this.get<{ conversation: any[] }>(`/conversations/${transactionId}`);
    return result.conversation;
  }

  /**
   * Send message in conversation (v1 - legacy)
   */
  async sendMessage(transactionId: string, message: string): Promise<{ success: boolean }> {
    await this.post(`/conversations/${transactionId}/messages`, { message });
    return { success: true };
  }

  /**
   * Get conversation for specific transaction (v2 - new API)
   */
  async getConversationV2(transactionId: string, includeWall = false): Promise<{
    type: string;
    transactionId: string;
    conversation: ConversationMessage[];
    posts?: any[];
  }> {
    const params = new URLSearchParams({
      type: 'transaction',
      transactionId: transactionId
    });
    if (includeWall) {
      params.append('includeWall', 'true');
    }
    
    // Use v2 endpoint - BaseApiClient will detect v2/ prefix and use /api/ prefix
    const result = await this.get<{
      type: string;
      transactionId: string;
      conversation: ConversationMessage[];
      posts?: any[];
    }>(`v2/conversations?${params}`);
    return result;
  }

  /**
   * Send message in transaction conversation (v2 - new API)
   */
  async sendMessageV2(transactionId: string, message: string): Promise<{ success: boolean }> {
    // Use v2 endpoint - BaseApiClient will detect v2/ prefix and use /api/ prefix
    console.log('🔵 ContactsApiClient.sendMessageV2 called:', {
      transactionId,
      messageLength: message.length,
      endpoint: 'v2/conversations'
    });
    
    try {
      const result = await this.post('v2/conversations', {
        type: 'transaction',
        transactionId: transactionId,
        message: message
      });
      console.log('✅ ContactsApiClient.sendMessageV2 success:', result);
      return { success: true };
    } catch (error: any) {
      console.error('❌ ContactsApiClient.sendMessageV2 error:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  }

  /**
   * Get direct conversation with contact (v2 API)
   * Uses userId parameter (contact.id maps to userId)
   * 
   * Note: The API uses 'userId' parameter, not 'contactId'
   * The contact.id from the Contact interface is the user ID
   */
  async getDirectConversationV2(contactId: string): Promise<{
    type: string;
    conversation: ConversationMessage[];
  }> {
    const params = new URLSearchParams({
      userId: contactId  // API uses 'userId', contact.id is the user ID
    });
    
    // Use v2 endpoint - BaseApiClient will detect v2/ prefix and use /api/ prefix
    const result = await this.get<{
      type: string;
      conversation: ConversationMessage[];
    }>(`v2/conversations?${params}`);
    return result;
  }

  /**
   * Send direct message to contact (v2 API)
   * Uses 'to' parameter (contact.id maps to recipient user ID)
   * 
   * Note: The API uses 'to' parameter, not 'contactId'
   * The contact.id from the Contact interface is the user ID
   */
  async sendDirectMessageV2(contactId: string, message: string): Promise<{ success: boolean }> {
    console.log('🔵 ContactsApiClient.sendDirectMessageV2 called:', {
      contactId,
      messageLength: message.length,
      endpoint: 'v2/conversations',
      note: 'Using "to" parameter for direct messaging'
    });
    
    try {
      const result = await this.post('v2/conversations', {
        to: contactId,  // API uses 'to', contact.id is the user ID
        message: message  // Can also use 'text' according to API docs
      });
      console.log('✅ ContactsApiClient.sendDirectMessageV2 success:', result);
      return { success: true };
    } catch (error: any) {
      console.error('❌ ContactsApiClient.sendDirectMessageV2 error:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    }
  }

  /**
   * Upload attachment to conversation
   */
  async uploadAttachment(file: File, transactionId: string): Promise<{
    id: string;
    filename: string;
    url: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transactionId', transactionId);

    const response = await fetch(`${this.baseURL}/conversations/attachments`, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload attachment');
    }

    return response.json();
  }

  /**
   * Mark conversation as read
   */
  async markConversationAsRead(conversationId: string): Promise<void> {
    await this.post(`/conversations/${conversationId}/read`);
  }

  /**
   * Archive conversation
   */
  async archiveConversation(conversationId: string): Promise<void> {
    await this.post(`/conversations/${conversationId}/archive`);
  }

  /**
   * Get unread message count
   */
  async getUnreadMessageCount(): Promise<{ count: number }> {
    return this.get<{ count: number }>('/conversations/unread-count');
  }

  /**
   * Get contacts summary for inbox view (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3524
   * This is the recommended endpoint for building a messaging inbox view.
   * 
   * @param includeRead Include contacts with no unread items (default: false)
   * @returns Contacts summary with unread counts for messages and posts
   */
  async getContactsSummary(includeRead: boolean = false): Promise<ContactsSummaryResponse> {
    const params = new URLSearchParams();
    if (includeRead) {
      params.append('includeRead', 'true');
    }
    
    const endpoint = `v2/user/contacts-summary${params.toString() ? `?${params.toString()}` : ''}`;
    
    try {
      return await this.get<ContactsSummaryResponse>(endpoint);
    } catch (error: any) {
      // Check if it's a 500 error (backend endpoint not implemented)
      const errorMessage = error?.message || String(error) || '';
      const isServerError = errorMessage.includes('500') || 
                           errorMessage.includes('Internal Server Error') ||
                           errorMessage.includes('this.app.get is not a function');
      
      if (isServerError) {
        // Return empty response instead of throwing to prevent breaking pages that don't need contacts
        // This allows pages like AdminSettings to load even if contacts endpoint is broken
        console.warn('⚠️ Contacts summary endpoint error, returning empty response:', errorMessage);
        return {
          contacts: [],
          totalUnreadCount: 0,
          totalUnreadPosts: 0
        } as ContactsSummaryResponse;
      }
      
      // Re-throw other errors (like 401, 403, network errors)
      throw error;
    }
  }

  /**
   * Get direct messages with a specific contact (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3610
   * 
   * @param contactId The contact/user ID
   * @param options Pagination and filtering options
   * @returns Messages with the contact
   */
  async getContactMessages(
    contactId: string,
    options?: {
      limit?: number;
      offset?: number;
      since?: string;
    }
  ): Promise<ContactMessagesResponse> {
    const params = new URLSearchParams();
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.offset) {
      params.append('offset', options.offset.toString());
    }
    if (options?.since) {
      params.append('since', options.since);
    }
    
    const queryString = params.toString();
    const endpoint = `v2/user/contact/${contactId}/messages${queryString ? `?${queryString}` : ''}`;
    
    return this.get<ContactMessagesResponse>(endpoint);
  }

  /**
   * Get posts from a specific contact (if merchant) (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3580
   * 
   * @param contactId The contact/user ID (merchant)
   * @param options Pagination and filtering options
   * @returns Posts from the contact
   */
  async getContactPosts(
    contactId: string,
    options?: {
      limit?: number;
      offset?: number;
      since?: string;
    }
  ): Promise<ContactPostsResponse> {
    const params = new URLSearchParams();
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.offset) {
      params.append('offset', options.offset.toString());
    }
    if (options?.since) {
      params.append('since', options.since);
    }
    
    const queryString = params.toString();
    const endpoint = `v2/user/contact/${contactId}/posts${queryString ? `?${queryString}` : ''}`;
    
    return this.get<ContactPostsResponse>(endpoint);
  }

  /**
   * Mark a direct message as read (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3641
   * 
   * @param contactId The contact/user ID
   * @param messageId The message ID to mark as read
   * @returns Success confirmation with read timestamp
   */
  async markContactMessageAsRead(
    contactId: string,
    messageId: string
  ): Promise<{ success: boolean; messageId: string; readAt: string }> {
    const endpoint = `v2/user/contact/${contactId}/messages/${messageId}/read`;
    
    return this.put<{ success: boolean; messageId: string; readAt: string }>(endpoint, {});
  }
} 